import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'src/store';
import {
  loadEngagementTimeSheets,
  loadTotalEngagementTimeSheet,
  createEngagementTimeSheet,
  updateEngagementTimeSheet,
  deleteEngagementTimeSheet,
  EngagementTimeSheetsSliceStateType,
  EngagementTimeSheetsSliceActionsType,
} from 'src/store/talents/timeSheetsSlice';
import { payPeriodsApi } from 'src/services/PayPeriods';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import { fillEngagementTimeSheetDates } from 'src/utils/engagementTimeSheets';
import getFetchStatus from 'src/utils/fetchStatus';
import { getCurrentPeriod, isDateInPayPeriod, toDateInterval } from 'src/utils/payPeriods';
import { IndexParams, CreateParams, UpdateParams } from 'src/repositories/talents/TimeSheetsRepository';

const selectPayPeriods = payPeriodsApi.endpoints.getPayPeriods.select();

const selectEngagementTimeSheets = createSelector(
  (state: RootState) => state.talentsTimeSheets.engagementTimeSheets,
  (state: RootState) => state.talentsTimeSheets.payPeriod,
  (state: RootState) => selectPayPeriods({ api: state.api }).data,
  (engagementTimeSheets, payPeriod, periods) => {
    if (!payPeriod) return engagementTimeSheets;

    const loadedPayPeriod = toDateInterval(payPeriod);
    const currentPeriod = getCurrentPeriod(periods ?? []);
    const isEditablePayPeriod = Boolean(currentPeriod && isDateInPayPeriod(loadedPayPeriod.start, currentPeriod));

    return fillEngagementTimeSheetDates(engagementTimeSheets, loadedPayPeriod, isEditablePayPeriod);
  },
);

type UseEngagementTimeSheetsType = Pick<
  EngagementTimeSheetsSliceStateType,
  'engagementTimeSheets' | 'totalEngagementTimeSheet' | 'isAdjustableTimesheets' | 'meta'
> &
  EngagementTimeSheetsSliceActionsType & {
    isEngagementTimeSheetsLoadFinished: boolean;
    isEngagementTimeSheetsCreatePending: boolean;
    isEngagementTimeSheetsDeletePending: boolean;
    isTotalEngagementTimeSheetLoadFinished: boolean;
  };

const useTimeSheets = (): UseEngagementTimeSheetsType => {
  const dispatch = useAppDispatch();
  const {
    meta,
    totalEngagementTimeSheet,
    isAdjustableTimesheets,
    index: { fetchStatus: timeSheetsIndexStatus },
    create: { fetchStatus: timeSheetsCreateStatus },
    delete: { fetchStatus: timeSheetsDeleteStatus },
    total: { fetchStatus: timeSheetsTotalStatus },
  } = useAppSelector(state => state.talentsTimeSheets);
  const engagementTimeSheets = useAppSelector(selectEngagementTimeSheets);

  const isEngagementTimeSheetsLoadFinished = getFetchStatus(timeSheetsIndexStatus).isFinished;
  const isEngagementTimeSheetsCreatePending = getFetchStatus(timeSheetsCreateStatus).isPending;
  const isEngagementTimeSheetsDeletePending = getFetchStatus(timeSheetsDeleteStatus).isPending;
  const isTotalEngagementTimeSheetLoadFinished = getFetchStatus(timeSheetsTotalStatus).isFinished;

  return {
    meta,
    engagementTimeSheets,
    totalEngagementTimeSheet,
    isAdjustableTimesheets,
    isEngagementTimeSheetsLoadFinished,
    isEngagementTimeSheetsCreatePending,
    isEngagementTimeSheetsDeletePending,
    isTotalEngagementTimeSheetLoadFinished,
    loadEngagementTimeSheets: (params?: IndexParams) => dispatch(loadEngagementTimeSheets(params)),
    loadTotalEngagementTimeSheet: (params?: IndexParams) => dispatch(loadTotalEngagementTimeSheet(params)),
    createEngagementTimeSheet: (params: CreateParams) => dispatch(createEngagementTimeSheet(params)),
    updateEngagementTimeSheet: (params: UpdateParams) => dispatch(updateEngagementTimeSheet(params)),
    deleteEngagementTimeSheet: (id: number) => dispatch(deleteEngagementTimeSheet(id)),
  };
};

export default useTimeSheets;
